/**
 * Form Visualizer v1 (Phase 3 Tier 3) — pure-function core.
 *
 * Two sub-modes: Form Explore (visual-first free-explore) and Form ID Quiz
 * (5-question audio-first session). Four canonical forms (12-bar blues,
 * 32-bar AABA, Verse-Chorus, Strophic) × one key (C major) × 100 BPM. All
 * tables are Bowie-locked verbatim from the scope memo; the V↔C rotation
 * (pop loop vs anti-pop loop) and the IV-iv-iii-vi B-section modal
 * interchange are pedagogically deliberate.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §4-§6
 * Bowie scope memo: .claude/agent-memory/bowie/project_form_visualizer_v1_scope.md
 */

import type { ChordVariant } from '../chord/chord-types.js'

export type FormKind = '12-bar-blues' | '32-bar-aaba' | 'verse-chorus' | 'strophic'
export type SectionKind =
  | 'intro'
  | 'verse'
  | 'chorus'
  | 'bridge'
  | 'outro'
  | 'a-jazz'
  | 'b-jazz'
  | 'blues-12'

export interface FormSectionDef {
  kind: SectionKind
  lexiconId: string
  /** CSS variable name (no `var(...)` wrapping). */
  colorToken: string
  measuresInSection: number
  progression: readonly ChordVariant[]
}

export interface FormEntry {
  kind: FormKind
  lexiconId: string
  displayName: string
  displayNameShort: string
  sections: readonly FormSectionDef[]
  /** Indices into `sections` selected for the Form ID Quiz audition (length 3). */
  quizSectionIndices: readonly [number, number, number]
}

// ─── Section-level chord progressions in C major (Bowie scope memo §"Canonical section progressions") ───

const C: ChordVariant = { root: 'C', variant: '' }
const G: ChordVariant = { root: 'G', variant: '' }
const Am: ChordVariant = { root: 'A', variant: 'm' }
const F: ChordVariant = { root: 'F', variant: '' }
const Em: ChordVariant = { root: 'E', variant: 'm' }
const Dm: ChordVariant = { root: 'D', variant: 'm' }
const Fm: ChordVariant = { root: 'F', variant: 'm' }

const VERSE_PROG: readonly ChordVariant[] = [C, G, Am, F, C, G, Am, F] // I–V–vi–IV ×2 (pop loop)
const CHORUS_PROG: readonly ChordVariant[] = [Am, F, C, G, Am, F, C, G] // vi–IV–I–V ×2 (anti-pop loop)
const BRIDGE_POP_PROG: readonly ChordVariant[] = [Em, Am, Dm, G, Em, Am, Dm, G] // iii–vi–ii–V ×2
const INTRO_PROG: readonly ChordVariant[] = [C, C, C, C] // I vamp ×4
const OUTRO_PROG: readonly ChordVariant[] = [C, C, C, C] // I vamp ×4 (mirrors intro)
const A_JAZZ_PROG: readonly ChordVariant[] = [C, Am, Dm, G, C, Am, Dm, G] // I–vi–ii–V turnaround ×2
const B_JAZZ_PROG: readonly ChordVariant[] = [F, Fm, Em, Am, F, Fm, Em, Am] // IV–iv–iii–vi modal interchange ×2
const BLUES_12_PROG: readonly ChordVariant[] = [C, C, C, C, F, F, C, C, G, F, C, G] // the 12-bar form

// ─── Section catalog definitions ───

const sectionIntro: FormSectionDef = {
  kind: 'intro',
  lexiconId: 'form-section-intro',
  colorToken: '--tuna-palette-cyan-500',
  measuresInSection: 4,
  progression: INTRO_PROG,
}
const sectionVerse: FormSectionDef = {
  kind: 'verse',
  lexiconId: 'form-section-verse',
  colorToken: '--tuna-palette-green-500',
  measuresInSection: 8,
  progression: VERSE_PROG,
}
const sectionChorus: FormSectionDef = {
  kind: 'chorus',
  lexiconId: 'form-section-chorus',
  colorToken: '--tuna-palette-amber-500',
  measuresInSection: 8,
  progression: CHORUS_PROG,
}
const sectionBridge: FormSectionDef = {
  kind: 'bridge',
  lexiconId: 'form-section-bridge',
  colorToken: '--tuna-palette-purple-500',
  measuresInSection: 8,
  progression: BRIDGE_POP_PROG,
}
const sectionOutro: FormSectionDef = {
  kind: 'outro',
  lexiconId: 'form-section-outro',
  colorToken: '--tuna-palette-cyan-500',
  measuresInSection: 4,
  progression: OUTRO_PROG,
}
const sectionAJazz: FormSectionDef = {
  kind: 'a-jazz',
  lexiconId: 'form-section-a-jazz',
  colorToken: '--tuna-palette-blue-500',
  measuresInSection: 8,
  progression: A_JAZZ_PROG,
}
const sectionBJazz: FormSectionDef = {
  kind: 'b-jazz',
  lexiconId: 'form-section-b-jazz',
  colorToken: '--tuna-palette-purple-500',
  measuresInSection: 8,
  progression: B_JAZZ_PROG,
}
const sectionBlues12: FormSectionDef = {
  kind: 'blues-12',
  lexiconId: 'form-12-bar-blues',
  colorToken: '--tuna-palette-orange-500',
  measuresInSection: 12,
  progression: BLUES_12_PROG,
}

/**
 * Bowie-locked form pool. Order matters — the welcome form-card grid renders
 * in this order: blues, AABA, V-C, strophic. Do not reorder.
 *
 * Quiz section indices encode Bowie scope memo §8 question 4 — each form's
 * audition picks 3 sections that capture the form's signature (return-pattern
 * for AABA, alternation for V-C, pure repetition for strophic). The 12-bar
 * blues uses a different audition path (split into 3 chunks of 2 measures
 * inside `generateFormQuizQuestion`), so its `quizSectionIndices` is
 * `[0, 0, 0]` as a sentinel (never read for the blues branch).
 */
export const FORM_POOL: readonly FormEntry[] = [
  {
    kind: '12-bar-blues',
    lexiconId: 'form-12-bar-blues',
    displayName: '12-bar blues',
    displayNameShort: '12-bar',
    sections: [sectionBlues12],
    quizSectionIndices: [0, 0, 0],
  },
  {
    kind: '32-bar-aaba',
    lexiconId: 'form-32-bar-aaba',
    displayName: '32-bar AABA',
    displayNameShort: 'AABA',
    sections: [sectionAJazz, sectionAJazz, sectionBJazz, sectionAJazz],
    quizSectionIndices: [0, 2, 3],
  },
  {
    kind: 'verse-chorus',
    lexiconId: 'form-verse-chorus',
    displayName: 'Verse-Chorus',
    displayNameShort: 'V-C',
    sections: [
      sectionIntro,
      sectionVerse,
      sectionChorus,
      sectionVerse,
      sectionChorus,
      sectionBridge,
      sectionChorus,
      sectionOutro,
    ],
    quizSectionIndices: [1, 2, 3],
  },
  {
    kind: 'strophic',
    lexiconId: 'form-strophic',
    displayName: 'Strophic',
    displayNameShort: 'Strophic',
    sections: [sectionVerse, sectionVerse, sectionVerse, sectionVerse],
    quizSectionIndices: [0, 1, 2],
  },
]

// ─── Quiz machinery ───

export interface FormQuizQuestion {
  entry: FormEntry
  auditionSections: readonly [FormSectionDef, FormSectionDef, FormSectionDef]
}

const AUDITION_MEASURES = 2

/**
 * Truncate a section's progression to the first `m` measures' worth of
 * chord-time. Returns at least 1 chord. Used by all forms except 12-bar
 * blues (which has its own bar-chunk split).
 */
const truncateToMeasures = (def: FormSectionDef, m: number): FormSectionDef => {
  const chordsPerMeasure = def.progression.length / def.measuresInSection
  const keepCount = Math.max(1, Math.round(chordsPerMeasure * m))
  return { ...def, progression: def.progression.slice(0, keepCount), measuresInSection: m }
}

/**
 * Synthesise three 2-measure pseudo-sections from the single 12-bar blues
 * block: bars 1-2 (I-vamp opening), bars 5-6 (IV move — the form's most
 * recognisable harmonic event), bars 11-12 (turnaround — signals "loop").
 */
const splitBluesAudition = (
  def: FormSectionDef
): readonly [FormSectionDef, FormSectionDef, FormSectionDef] => [
  { ...def, progression: def.progression.slice(0, 2), measuresInSection: 2 },
  { ...def, progression: def.progression.slice(4, 6), measuresInSection: 2 },
  { ...def, progression: def.progression.slice(10, 12), measuresInSection: 2 },
]

/**
 * Generate a fresh random form-quiz question.
 *
 * RNG draw order:
 *  1. `rng()` → form index into FORM_POOL (4 entries).
 *
 * The audition sections are derived deterministically from the chosen form's
 * `quizSectionIndices`, with two compression paths:
 *   - 12-bar blues: split the single block into bars 1-2 / 5-6 / 11-12
 *   - all other forms: take the named sections and truncate each to 2 measures
 */
export const generateFormQuizQuestion = (
  rng: () => number = Math.random
): FormQuizQuestion => {
  const entry = FORM_POOL[Math.floor(rng() * FORM_POOL.length)]
  let auditionSections: readonly [FormSectionDef, FormSectionDef, FormSectionDef]
  if (entry.kind === '12-bar-blues') {
    auditionSections = splitBluesAudition(entry.sections[0])
  } else {
    const [i0, i1, i2] = entry.quizSectionIndices
    auditionSections = [
      truncateToMeasures(entry.sections[i0], AUDITION_MEASURES),
      truncateToMeasures(entry.sections[i1], AUDITION_MEASURES),
      truncateToMeasures(entry.sections[i2], AUDITION_MEASURES),
    ]
  }
  return { entry, auditionSections }
}

export interface FormQuizVerdict {
  correct: boolean
  picked: FormKind
  entry: FormEntry
}

export const validateFormQuizAnswer = (
  q: FormQuizQuestion,
  picked: FormKind
): FormQuizVerdict => ({
  correct: picked === q.entry.kind,
  picked,
  entry: q.entry,
})

export interface FormQuizResult {
  question: FormQuizQuestion
  verdict: FormQuizVerdict
}

export interface FormSessionStats {
  total: number
  correct: number
}

export const computeFormSessionStats = (
  results: FormQuizResult[]
): FormSessionStats => ({
  total: results.length,
  correct: results.filter(r => r.verdict.correct).length,
})
